import React from 'react';
import clsx from 'clsx';
import { getPropertyByPath } from '@repo/checkout/theming/themePropertyRegistry';

// The chip grid behind every one-click preset (price option layouts, order bump
// card styles). A preset sets EVERY key it governs, so switching presets can
// never leave an option dangling from the previous one, and the active chip is
// DERIVED from the current values rather than persisted. An unset value reads
// as what the card renders for it — the registry default — so a fresh page
// lights the chip that IS the default, and a page with one field moved lights
// the chip that field lands on rather than whichever chip happens to match its
// own gaps. A key with no registry default falls back to the preset's own.
//
// Detection reads `keys`, plus any `detect` predicates a preset carries.
// `extras` — an element toggle, a size the preset needs to read right — are
// apply-only: nudging one afterwards leaves the chip highlighted, because the
// preset's own choices have not changed.
//
// A `detect` predicate is the middle ground between the two, for an extra a
// seller is otherwise free to move but which changes what the card IS at one
// particular value: it asks a question of the field rather than pinning it.
//
// `freeKeys` runs the other way, for a preset whose own arrangement already
// settles a key: it still SETS the key on click, but no longer matches on it,
// so a seller who moves that key afterwards keeps the chip.
//
// `customLabel` adds a last chip that lights when no preset matches and applies
// nothing. Without it every chip sits dark, which reads as a broken picker
// rather than as a card the seller has taken past any preset.

const getNested = (obj, path) =>
  path.split('.').reduce((node, key) => (node == null ? undefined : node[key]), obj);

export default function PresetGrid({
  base,
  keys,
  presets,
  values,
  setFieldValue,
  renderThumb,
  customLabel,
}) {
  const resolve = (preset, key) =>
    getNested(values, `${base}.${key}`) ??
    getPropertyByPath(`${base}.${key}`)?.defaultValue ??
    preset.values[key];

  const matches = preset =>
    keys
      .filter(key => !preset.freeKeys?.includes(key))
      .every(key => resolve(preset, key) === preset.values[key]) &&
    Object.entries(preset.detect || {}).every(([key, predicate]) =>
      predicate(getNested(values, `${base}.${key}`)),
    );

  const active = presets.find(preset => matches(preset)) || null;

  const apply = preset => {
    const writes = [
      ...keys.map(key => [`${base}.${key}`, preset.values[key]]),
      ...Object.entries(preset.extras || {}).map(([key, value]) => [`${base}.${key}`, value]),
    ];
    // Formik validates, and re-renders, on every write it is not told to skip;
    // once for the lot is enough.
    writes.forEach(([path, value], i) => setFieldValue(path, value, i === writes.length - 1));
  };

  return (
    // mb-2 doubles the group's own gap-2 into the parent's gap-y-2 below it:
    // matching the two left the chips reading as rows of the fields underneath
    // rather than as a block the fields hang off.
    <div className="mb-2 grid grid-cols-2 gap-2">
      {presets.map(preset => (
        <Chip
          key={preset.key}
          name={preset.name}
          isActive={active?.key === preset.key}
          onClick={() => apply(preset)}
        >
          {renderThumb(preset.key)}
        </Chip>
      ))}
      {customLabel ? (
        <Chip name={customLabel} isActive={!active}>
          {renderThumb('custom')}
        </Chip>
      ) : null}
    </div>
  );
}

// Without an onClick the chip is a state, not a choice: it still reads as
// pressed or not, but there is nothing to apply.
function Chip({ name, isActive, onClick, children }) {
  return (
    <button
      type="button"
      aria-pressed={isActive}
      onClick={onClick}
      disabled={!onClick}
      className={clsx(
        'relative block w-full rounded-md border px-3 py-2 text-center text-sm font-medium outline-none transition-colors',
        'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        onClick ? 'cursor-pointer' : 'cursor-default',
        isActive ? 'border-primary' : 'border-input',
        { 'hover:bg-accent/50': onClick && !isActive },
      )}
    >
      <div className="px-6">
        {/* A fixed box, so a thumb that stacks (an image above its copy)
            cannot make its chip taller than the ones beside it. */}
        <div className="flex h-[52px] items-center justify-center">{children}</div>
        <div className="mt-2 text-xs font-normal text-gray-900 dark:text-foreground">{name}</div>
      </div>
      <span
        className={clsx(
          'absolute right-2 top-2 flex size-4 items-center justify-center rounded-full border transition-colors',
          isActive ? 'border-primary' : 'border-input',
        )}
      >
        {isActive && <span className="size-2 rounded-full bg-primary" />}
      </span>
    </button>
  );
}
